import {
  Box,
  Button,
  ColumnLayout,
  ExpandableSection,
  Grid,
  Header,
  Modal,
  SpaceBetween,
  StatusIndicator,
} from '@amzn/awsui-components-react';
import CodeView from '@amzn/awsui-code-view/code-view';
import pythonHighlight from '@amzn/awsui-code-view/highlight/python';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { i18nKeys } from '@/src/utils/i18n.utils';
import { Challenge, ChallengeTask } from '@/src/types/Challenge';

import {
  ChallengeValidationState,
  ChallengeValidationStatus,
  TaskValidationResult,
} from '@amzn/aws-jam-typescript-client';

export interface ChallengeValidationDetailModalProps {
  run: ChallengeValidationState;
  challenge: Challenge;
}

const getStatusIndicatorType = (
  status: ChallengeValidationStatus | string
): 'success' | 'error' | 'in-progress' | 'warning' | 'info' => {
  switch (status) {
    case ChallengeValidationStatus.PASSED:
      return 'success';
    case ChallengeValidationStatus.FAILED:
    case ChallengeValidationStatus.ERROR:
      return 'error';
    case ChallengeValidationStatus.SOLVING:
    case ChallengeValidationStatus.WAITING_FOR_LAB:
    case ChallengeValidationStatus.VALIDATING:
      return 'in-progress';
    default:
      return 'info';
  }
};

const formatDate = (date: Date | undefined): string => {
  if (!date) {
    return '-';
  }

  let d = new Date(date);

  if (d.getFullYear() > 2100) {
    d = new Date(d.getTime() / 1000);
  }

  if (d.getFullYear() < 2000) {
    const raw = typeof date === 'number' ? (date as number) : d.getTime();
    d = new Date(raw * 1000);
  }

  return d.toLocaleString();
};

interface TaskDetail {
  taskNumber: number;
  taskId: string;
  result?: TaskValidationResult;
  code?: string;
  userInput?: string;
}

/**
 * Merge taskResults, taskSolverCode, and taskUserInputs into a unified list
 * of task details, sorted by ascending task number.
 * Uses the challenge's task list as the source of truth for task ID → number mapping.
 */
const buildTaskDetails = (run: ChallengeValidationState, challenge: Challenge): TaskDetail[] => {
  // Build ID → task number map from the challenge definition
  const taskIdToNumber: Record<string, number> = {};
  if (challenge.props?.tasks) {
    for (const task of challenge.props.tasks as ChallengeTask[]) {
      if (task.id) {
        taskIdToNumber[task.id] = task.taskNumber;
      }
    }
  }

  const taskMap = new Map<string, TaskDetail>();

  // Populate from taskResults
  if (run.taskResults) {
    for (const result of run.taskResults) {
      if (result.taskId) {
        taskMap.set(result.taskId, {
          taskNumber: taskIdToNumber[result.taskId] ?? (result.taskNumber as number) ?? 0,
          taskId: result.taskId,
          result,
        });
      }
    }
  }

  // Merge solver code
  if (run.taskSolverCode) {
    for (const [taskId, solve] of Object.entries(run.taskSolverCode)) {
      const existing = taskMap.get(taskId);

      if (existing) {
        existing.code = solve.code;
        existing.userInput = solve.userInput;
      } else {
        taskMap.set(taskId, {
          taskNumber: taskIdToNumber[taskId] ?? 0,
          taskId,
          code: solve.code,
          userInput: solve.userInput,
        });
      }
    }
  }

  // Merge user inputs
  if (run.taskUserInputs) {
    for (const [taskId, input] of Object.entries(run.taskUserInputs)) {
      const existing = taskMap.get(taskId);

      if (existing) {
        existing.userInput = input;
      } else {
        taskMap.set(taskId, {
          taskNumber: taskIdToNumber[taskId] ?? 0,
          taskId,
          userInput: input,
        });
      }
    }
  }

  return [...taskMap.values()].sort((a, b) => a.taskNumber - b.taskNumber);
};

export const ChallengeValidationDetailModal = NiceModal.create((props: ChallengeValidationDetailModalProps) => {
  const { run, challenge } = props;
  const modal = useModal();
  const { t } = useTranslation();

  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>(undefined);

  const handleClose = () => {
    setSelectedTaskId(undefined);
    void modal.hide();
  };

  const getStatusLabel = (status: ChallengeValidationStatus | string): string => {
    switch (status) {
      case ChallengeValidationStatus.SOLVING:
        return t(i18nKeys.challenges.challengeDetails.validation.status.solving);
      case ChallengeValidationStatus.WAITING_FOR_LAB:
        return t(i18nKeys.challenges.challengeDetails.validation.status.waitingForLab);
      case ChallengeValidationStatus.VALIDATING:
        return t(i18nKeys.challenges.challengeDetails.validation.status.validating);
      case ChallengeValidationStatus.PASSED:
        return t(i18nKeys.challenges.challengeDetails.validation.status.passed);
      case ChallengeValidationStatus.FAILED:
        return t(i18nKeys.challenges.challengeDetails.validation.status.failed);
      case ChallengeValidationStatus.ERROR:
        return t(i18nKeys.challenges.challengeDetails.validation.status.error);
      default:
        return status;
    }
  };

  const getTaskResultIndicator = (result?: TaskValidationResult) => {
    if (!result) {
      return null;
    }

    if (result.success === true) {
      return (
        <StatusIndicator type="success">
          {t(i18nKeys.challenges.challengeDetails.validation.status.passed)}
        </StatusIndicator>
      );
    }

    if (result.success === false) {
      return (
        <StatusIndicator type="error">
          {t(i18nKeys.challenges.challengeDetails.validation.status.failed)}
        </StatusIndicator>
      );
    }

    return (
      <StatusIndicator type="pending">
        {t(i18nKeys.challenges.challengeDetails.validation.status.pending)}
      </StatusIndicator>
    );
  };

  const taskDetails = buildTaskDetails(run, challenge);

  let selectedTask: TaskDetail | undefined;

  for (const task of taskDetails) {
    if (task.taskId === selectedTaskId) {
      selectedTask = task;
      break;
    }
  }

  const taskList = (
    <SpaceBetween size="l" direction="vertical">
      {/* Run summary */}
      <ColumnLayout columns={2} variant="text-grid">
        <div>
          <Box variant="awsui-key-label">{t(i18nKeys.challenges.challengeDetails.validation.labels.status)}</Box>
          <StatusIndicator type={getStatusIndicatorType(run.status)}>{getStatusLabel(run.status)}</StatusIndicator>
        </div>

        <div>
          <Box variant="awsui-key-label">{t(i18nKeys.challenges.challengeDetails.validation.labels.version)}</Box>
          <span>{run.challengeVersion ?? '-'}</span>
        </div>

        <div>
          <Box variant="awsui-key-label">{t(i18nKeys.challenges.challengeDetails.validation.labels.progress)}</Box>
          <span>
            {run.completedTasks ?? 0} / {run.totalTasks ?? '?'}
            {run.failedTasks ? ` (${String(run.failedTasks)} failed)` : ''}
          </span>
        </div>

        <div>
          <Box variant="awsui-key-label">{t(i18nKeys.challenges.challengeDetails.validation.labels.runAt)}</Box>
          <span>{formatDate(run.runAt)}</span>
        </div>
      </ColumnLayout>

      {run.useCachedActionPlans && run.taskSolverCode && (
        <StatusIndicator type="info">
          {t(i18nKeys.challenges.challengeDetails.validation.modal.cachedCommands)}
        </StatusIndicator>
      )}

      {run.errorMessage && <StatusIndicator type="error">{run.errorMessage}</StatusIndicator>}

      {/* Per-task details */}
      {taskDetails.map((task) => (
        <ExpandableSection
          key={task.taskId}
          variant="default"
          defaultExpanded
          header={
            <SpaceBetween size="s" direction="horizontal" alignItems="center">
              <span>Task {task.taskNumber}</span>
              {getTaskResultIndicator(task.result)}
              {task.result?.durationMs != null && (
                <Box color="text-body-secondary">{(task.result.durationMs / 1000).toFixed(1)}s</Box>
              )}
            </SpaceBetween>
          }
          data-testid={`task-detail-${String(task.taskNumber)}`}
        >
          <SpaceBetween size="m" direction="vertical">
            {task.code && (
              <Button
                variant={task.taskId === selectedTaskId ? 'primary' : 'normal'}
                onClick={() => setSelectedTaskId(task.taskId === selectedTaskId ? undefined : task.taskId)}
                data-testid={`view-code-task-${String(task.taskNumber)}`}
              >
                {t(i18nKeys.challenges.challengeDetails.validation.modal.viewCode)}
              </Button>
            )}

            {/* Input (submitted answer) */}
            {task.userInput && (
              <div>
                <Box variant="awsui-key-label" margin={{ bottom: 'xxs' }}>
                  {t(i18nKeys.challenges.challengeDetails.validation.modal.input)}
                </Box>
                <CodeView content={task.userInput} data-testid={`input-task-${String(task.taskNumber)}`} />
              </div>
            )}

            {/* Error */}
            {task.result?.error && (
              <div>
                <Box variant="awsui-key-label" margin={{ bottom: 'xxs' }}>
                  {t(i18nKeys.challenges.challengeDetails.validation.status.error)}
                </Box>
                <StatusIndicator type="error">{task.result.error}</StatusIndicator>
              </div>
            )}
          </SpaceBetween>
        </ExpandableSection>
      ))}
    </SpaceBetween>
  );

  const renderContent = () => {
    if (selectedTask?.code) {
      return (
        <Grid gridDefinition={[{ colspan: 4 }, { colspan: 8 }]}>
          {taskList}

          <SpaceBetween size="xs" direction="vertical" data-testid="solver-code-pane">
            <Header variant="h3">
              {`${t(i18nKeys.challenges.challengeDetails.validation.modal.solverCode)} — Task ${String(
                selectedTask.taskNumber
              )}`}
            </Header>

            <CodeView
              content={selectedTask.code}
              highlight={pythonHighlight}
              lineNumbers
              data-testid={`solver-code-task-${String(selectedTask.taskNumber)}`}
            />
          </SpaceBetween>
        </Grid>
      );
    }

    return taskList;
  };

  return (
    <Modal
      visible={modal.visible}
      onDismiss={handleClose}
      header={`${t(i18nKeys.challenges.challengeDetails.validation.modal.title)} — ${formatDate(run.runAt)}`}
      size={selectedTask ? 'x-large' : 'medium'}
      data-testid="validation-detail-modal"
      footer={
        <Box float="right">
          <Button variant="primary" onClick={handleClose}>
            {t(i18nKeys.general.close)}
          </Button>
        </Box>
      }
    >
      {renderContent()}
    </Modal>
  );
});
